// Blueprints
import type { Blueprint } from '../../base/blueprints/Blueprint';

// Entries
import type { Entry } from '../entries/Entry';
import type { RootEntry } from '../entries/RootEntry';
import { RootEntryAbstract, instanceOfRootEntry } from '../entries/RootEntry';
import type { EntryFactory } from '../traits/EntryFactory';
import { instanceOfEntryFactory } from '../traits/EntryFactory';
import type { ValidEntry } from '../entries/ValidEntry';
import { instanceOfValidEntry } from '../entries/ValidEntry';

// Managers
import type { SchemaManager } from '../../base/managers/SchemaManager';
import { instanceOfHasRepetitions } from '../../base/traits/HasRepetitions';

// --------------------------------------------------

export class DocumentManager
{
    private root: RootEntry;
    private schema: SchemaManager;
    private identity: () => number;

    public constructor(schemaManager: SchemaManager)
    {
        this.schema = schemaManager;
        this.setRootEntry(null);
    }

    public get id(): number
    {
        return this.identity ? this.identity() : 0;
    }

    public setIdentity(callback: () => number): void
    {
        this.identity = callback;
    }

    public getRootEntry(): Entry
    {
        return this.root;
    }

    public setRootEntry(entry: Entry | null): void
    {
        const blueprint = this.schema.getBlueprint();

        if (instanceOfEntryFactory(blueprint))
        {
            if (entry instanceof RootEntryAbstract)
            {
                this.root = entry;
            }
            else
            {
                if (entry == null)
                    entry = blueprint.createEntry({});

                if (!instanceOfRootEntry(entry))
                    throw new Error('Argument \'entry\' must be of type RootEntry.');

                entry = this.prepareRootEntry(entry as RootEntry);

                this.root = entry as RootEntry;
            }
        }
    }

    private prepareRootEntry(entry: RootEntry): RootEntry
    {
        const blueprint = this.schema.getBlueprint() as EntryFactory<any>;
        const root = blueprint.createEntry({});

        // Dla komponentów na poziomie głownym
        this.schema.components(null, blueprint).forEach((component) =>
        {
            if (instanceOfEntryFactory(component))
            {
                const data = component.name in entry ? entry[component.name].data : null;
                const parent = this.schema.parent(component);

                if (!instanceOfHasRepetitions(parent))
                {
                    root[component.name] = component.createEntry(data);
                }
            }
        });

        // Dla komponentów w tabelach
        this.schema.components(null, blueprint).forEach((component) =>
        {
            if (instanceOfEntryFactory(component))
            {
                const parent = this.schema.parent(component);

                if (instanceOfHasRepetitions(parent))
                {
                    const table = parent.name in entry ? entry[parent.name].data : [];

                    for (let i = 0; i < table.length; i++)
                    {
                        const row = table[i];
                        const data = component.name in row ? row[component.name].data : null;

                        row[component.name] = component.createEntry(data);
                    }

                    root[parent.name].data = table;
                }
            }
        });

        return root;
    }

    public initEntry(blueprint: Blueprint & EntryFactory<any>, entry: any, typeCheck: (entry: any) => boolean, index: number = -1): any
    {
        const parent = this.schema.parent(blueprint);
        const root = !instanceOfHasRepetitions(parent) ? this.root : (this.root[parent.name] ??= []);

        if (!(blueprint.name in root && typeCheck(root[blueprint.name])))
        {
            if (index > -1)
            {
                if (root.data && root.data[index] == undefined)
                {
                    root.data.push({});
                }

                root.data[index][blueprint.name] = Object.assign(entry, root.data[index][blueprint.name]); //
            }
            else
                root[blueprint.name] = entry;
        }

        return index > -1 ? root.data[index][blueprint.name] : root[blueprint.name];
    }

    public getEntry(blueprint: Blueprint & EntryFactory<any>): Entry
    {
        if (!(blueprint.name in this.root))
            this.root[blueprint.name] = blueprint.createEntry(null);

        return this.root[blueprint.name];
    }

    public replaceEntry(blueprint: Blueprint & EntryFactory<any>, factory: (original: Entry) => Entry): Entry
    {
        return (this.root[blueprint.name] = factory(this.getEntry(blueprint)));
    }

    public renameEntry(name: string, old: string): void
    {
        if (old && old.length > 0 && old in this.root)
        {
            this.root[name] = this.root[old];

            delete this.root[old];
        }
    }

    public removeEntry(name: string): void
    {
        if (name in this.root)
        {
            delete this.root[name];
        }
    }

    public setErrors(errors: Record<string, string[]>): void
    {
        Object.keys(this.root).forEach((property) =>
        {
            if (instanceOfValidEntry(this.root[property]))
            {
                const name = `${property.toLowerCase()}.`;
                const entry = this.root[property] as ValidEntry<any>;

                Object.entries(errors).filter(([key]) => key.startsWith(name)).forEach(([key, messages]) =>
                {
                    entry.errors[key.substring(name.length)] = messages;
                });
            }
        });
    }
}
